import { Button } from '@jotta/ui/Button'
import { getRouterStore } from '@jotta/router'
import { ErrorPage } from '@jotta/ui/ErrorPage'
import { t } from '@lingui/macro'
import { keepPreviousData } from '@tanstack/react-query'
import Debug from 'debug'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useSearchParams } from 'react-router-dom'
import { displayJoinFolderShareSuccessAlert } from '../FileActions/displayJoinFolderShareSuccessAlert'
import { FileCarousel } from '../FileCarousel/FileCarousel'
import { useFileStore } from '../FileStore/FileContext'
import { Folder } from '../Folder/Folder'
import { useInfinitePathItem, usePathItem } from '../PathItem/hooks/usePathItem'
import { usePendingInvites } from '../PathItem/hooks/usePendingInvites'
import styles from './FileRoute.module.scss'
import { sortByCookie, viewModeCookie } from './utils/cookies'
import { useParsedFileRouteParams } from './utils/parseFileRouteParams'
import { DisplayActiveFileActionDialog } from '../FileActions/DisplayActiveFileActionDialog'

const debug = Debug('jotta:files:FileRoute')
/**
 * Primary file route
 * Updates FileRouteStore when route changes
 */

function LoadMoreButton({
  showButton = false,
  loading = false,
  onClick,
  buttonDelay = 200,
  children,
}: React.PropsWithChildren<{
  showButton?: boolean
  loading?: boolean
  buttonDelay?: number
  onClick: React.MouseEventHandler<HTMLButtonElement>
}>) {
  const [showDelayed, setShowDelayed] = useState(showButton)

  useEffect(() => {
    if (!showButton) {
      setShowDelayed(false)
      return
    }

    const timer = window.setTimeout(() => {
      setShowDelayed(true)
    }, buttonDelay)

    return () => {
      if (timer) {
        window.clearTimeout(timer)
      }
    }
  }, [showButton, buttonDelay])

  const buttonVisible = showButton && showDelayed
  const spinnerVisible = loading || (showButton && !showDelayed)

  if (!spinnerVisible && !buttonVisible) {
    return null
  }
  return (
    <Button
      className="btn btn-outline"
      onClick={onClick}
      loading={spinnerVisible}
      disabled={spinnerVisible || !buttonVisible}
      sx={!buttonVisible ? { border: 'none' } : undefined}
    >
      {buttonVisible && children}
    </Button>
  )
}

export const FileRoute = observer(function FileRoute() {
  useLayoutEffect(() => {
    console.group(`FileRoute ${location.pathname}`)
    return () => {
      console.groupEnd()
    }
  }, [])
  const route = useParsedFileRouteParams()
  const store = useFileStore()
  const folder = store.folder
  const { apiPath } = folder
  /**
   * Carousel open state stays the same until current path item has fetched,
   * this prevents flashing when navigating between slides
   */
  const [carouselOpen, setCarouselOpen] = useState(false)
  const itemQuery = usePathItem({
    variables: {
      context: route.context,
      path: route.apiPath,
    },
  })
  const item = itemQuery.data

  useEffect(() => {
    if (item) {
      runInAction(() => {
        store.currentItem = item
        store.folder.addItem(item.item, Date.now())
      })
      setCarouselOpen(item.isFile)
    }
  }, [item, store])

  const { context, sortBy } = store.route
  const { folderApiPath } = store

  /**
   * Fetch current folder, with children
   * This fetch is disabled until the current path item has finished fetching and we
   * know if it's a file or a folder
   */
  const folderQuery = useInfinitePathItem({
    variables: {
      context,
      path: folderApiPath,
      sortBy,
    },
    retry: 1,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: Boolean(item),
  })

  const {
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = folderQuery

  const { ref, inView } = useInView({
    delay: 200,
    skip: isFetching,
  })

  useEffect(() => {
    if (folderQuery.data && folderQuery.data.pages[0].item.path === apiPath) {
      folder.handleInfiniteDataResponse(folderQuery.data)
      folder.hasNextPage = hasNextPage
    }
  }, [folder, folderQuery.data, apiPath, hasNextPage])

  useLayoutEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, hasNextPage])

  usePendingInvites(route.isSync && route.isRoot)

  useLayoutEffect(() => {
    if (isFetchingNextPage || isLoading) {
      folder.hasRendered = false
    }
  }, [isFetchingNextPage, isLoading, folder.hasRendered, folder])

  const hasRendered = folder.hasRendered

  const loading = useRef(true)

  if (
    loading.current &&
    folderQuery.isFetched &&
    !folderQuery.isLoading &&
    hasRendered
  ) {
    loading.current = false
  }

  const { data } = folderQuery

  useEffect(() => {
    if (data?.pages.length === 1 && hasNextPage && !isFetching) {
      fetchNextPage()
    }
  }, [hasNextPage, fetchNextPage, data, isFetching])

  if (itemQuery.isError) {
    return <ErrorPage hideFooter error={itemQuery.error} />
  }

  return (
    <>
      <Folder store={folder} loading={loading.current} key={route.apiPath} />
      <div ref={ref} className={styles.bottom}>
        <LoadMoreButton
          onClick={() => fetchNextPage()}
          loading={!hasRendered && !loading.current}
          showButton={
            !loading.current &&
            folder.hasFetched &&
            hasNextPage &&
            hasRendered &&
            inView
          }
          buttonDelay={2000}
        >
          {t`Load more files`}
        </LoadMoreButton>
      </div>
      <FileCarousel
        open={carouselOpen}
        context={route.context}
        loading={itemQuery.isLoading || folderQuery.isLoading}
        file={folder.currentFile}
        files={folder.carouselPathItems}
      />
      {!carouselOpen && <DisplayActiveFileActionDialog store={folder} />}
    </>
  )
})
/**
 * Handle redirects before rendering the route
 */
function HandleFileRouteRedirects() {
  const [search, setSearch] = useSearchParams()
  const openShare = search.get('openShare')
  // debug('openShare', openShare)
  const route = useParsedFileRouteParams()
  const { routePath, shouldRedirect, viewMode, sortBy } = route
  const { navigate } = getRouterStore()
  useEffect(() => {
    if (openShare) {
      // Slash in file name not allowed
      if (/\//.test(openShare)) {
        search.delete('openShare')
        setSearch(search)
        return
      }
      navigate(`/web/sync/list/name/${openShare}`, { replace: true })
      displayJoinFolderShareSuccessAlert(openShare)
      return
    }
    if (shouldRedirect) {
      navigate(routePath, { replace: true })
    } else {
      viewModeCookie.set(viewMode)
      sortByCookie.set(sortBy)
    }
  }, [
    openShare,
    navigate,
    routePath,
    shouldRedirect,
    search,
    setSearch,
    viewMode,
    sortBy,
  ])
  if (openShare || shouldRedirect) {
    return null
  }
  return <FileRoute />
}
/**
 * Export file route for lazy loading by react-router
 * The naming is significant, see https://reactrouter.com/en/main/route/route#lazy
 */
export const Component = HandleFileRouteRedirects
